import React, {Component} from 'react';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {FaIcon} from "../FaIcon";
import {Spinner} from "../Spinner";
import {Switch} from "../buttons/Swtich";
import {MultiSelect} from "../selects/MultiSelect";
import {ClientFeatureToggles} from "../client/ClientFeatureToggles";
import {HELP_ADMIN_AND_REGULAR_USERS, LinkToArticle} from "../help_center/LinkToArticle";
import { withTranslation } from 'react-i18next';
import {toast} from 'react-toastify';
import {ClientUsersLimitNotice} from "./ClientUsersLimitNotice";
import {LearningProfilePicker, learningRolesForType} from "./LearningProfilePicker";
import {PORTAL_LANGUAGES, portalLanguageOf} from "../portal/portalLanguage";
import {
    fetchClientPortalQuota,
    peekClientPortalQuota,
    refetchPortalSubscriptionGating,
} from "../../utils/usePortalSubscriptionGating";

// El copy de un error lo resuelve el FRONTEND por codigo, no el texto que mande el backend:
// asi el mensaje respeta el espanol neutro y no depende de lo que escriba cada capa. Hoy entraban
// a pantalla el `NETWORK_ERROR` de APIClient (hardcodeado y en voseo) y el `detail` de DRF para
// 401/403/500, en ingles. Un codigo sin clave cae al generico. Dos de los casos reusan la clave
// que ya existia, para no tener el mismo texto escrito dos veces.
const ERROR_MESSAGE_KEYS = {
    EMAIL_IS_AGENCY_USER: 'invite_user.error_email_is_agency_user',
    CLIENT_USERS_LIMIT_REACHED: 'invite_user.client_limit_reached',
    MISSING_CLIENT: 'invite_user.client_requires_at_least_one',
    NETWORK_ERROR: 'invite_user.error_network',
};

// `error_code` lo pone APIClient en el camino de error; `status` cubre las vistas que devuelven
// el codigo tipado con HTTP 200.
export function errorMessageKeyFor(response) {
    const code = response && (response.error_code || response.status);
    return ERROR_MESSAGE_KEYS[code] || 'invite_user.generic_error';
}

class InviteUserFormClass extends Component {
    constructor(props) {
        super(props);
        const editUser = this.props.editUser;
        if (editUser) {
            this.state = {
                saving: false,
                userType: editUser.user_type || 'Admin',
                canUseMarketingModule: true,
                canUseManageAccountsModule: false,
                moduleMarketingCanAssignClients: editUser.module_marketing_can_assign_clients || false,
                moduleManageAccountsGoogleManagerId: null,
                moduleManageAccountsCanEditManagerAdspentLimit: false,
                moduleManageAccountsCanCreateAdAccounts: false,
                moduleManageAccountsCanGiveAccessToAdAccounts: false,
                agencyClients: null,
                selectedClientIds: (editUser.client_ids || []).map(id => id.toString()),
                loadingClients: false,
                clientFeatures: editUser.client_features || {
                    dashboard: false, data_explorer: false, connect: true, reports: true, insights: false, connections: false
                }
            };
        } else {
            const hasFixedClient = this.props.fixedClientId != null;
            this.state = {
                saving: false,
                userType: (this.props.clientOnly || hasFixedClient) ? 'Client' : (this.props.moduleAccessConfigurationEnabled ? 'Regular' : 'Admin'),
                canUseMarketingModule: true,
                canUseManageAccountsModule: false,
                moduleMarketingCanAssignClients: !this.props.moduleAccessConfigurationEnabled,
                moduleManageAccountsGoogleManagerId: null,
                moduleManageAccountsCanEditManagerAdspentLimit: !this.props.moduleAccessConfigurationEnabled,
                moduleManageAccountsCanCreateAdAccounts: !this.props.moduleAccessConfigurationEnabled,
                moduleManageAccountsCanGiveAccessToAdAccounts: !this.props.moduleAccessConfigurationEnabled,
                agencyClients: null,
                selectedClientIds: hasFixedClient ? [this.props.fixedClientId.toString()] : [],
                loadingClients: false,
                clientFeatures: this.props.defaultClientFeatures || {
                    dashboard: true, data_explorer: false, connect: true, reports: true, insights: false, connections: false
                }
            };
        }
        this.state.emailLookup = null;
        // El perfil de Aprende Dashbo (sólo al invitar, sin cliente fijo): un Admin arranca con Liderazgo.
        this.state.learningRoles = learningRolesForType([], this.state.userType);
        // ¿Quien invita tocó el selector? Sólo entonces el perfil viaja (ver `learningRolesToSend`).
        this.state.learningRolesTouched = false;
        // El idioma del portal: arranca en el del portal del cliente (si quien abre el formulario lo
        // conoce) y viaja sólo si quien invita lo tocó (ver `portalLanguageToSend`).
        this.state.portalLanguage = portalLanguageOf(this.props.defaultPortalLanguage) || 'es';
        this.state.portalLanguageTouched = false;
        // El cupo de usuarios cliente, leído del mismo lugar que el resto del portal. Hace falta
        // en los flujos que no lo reciben por props (tab Portal, conexiones): antes esos sólo se
        // enteraban del límite por un toast DESPUÉS de completar el formulario, y sin salida.
        // Si ya está en el cache, se usa desde el primer render: sin esto el modal pintaba un
        // frame del estado sin cupo antes del real.
        this.state.clientQuota = this.props.editUser ? null : peekClientPortalQuota();
        this.state.limitRejected = false;
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleClientsChange = this.handleClientsChange.bind(this);
        this.handleSingleClientChange = this.handleSingleClientChange.bind(this);
        this.handleEmailBlur = this.handleEmailBlur.bind(this);
        this.handleEmailChange = this.handleEmailChange.bind(this);
        this.selectUserType = this.selectUserType.bind(this);
    }

    componentDidMount() {
        // Con cliente fijo (invitacion desde el Portal del cliente) no hace falta el selector de clientes.
        if (this.props.fixedClientId == null) {
            this.fetchAgencyClients();
        }
        if (!this.props.editUser) {
            fetchClientPortalQuota().then((clientQuota) => {
                // Un cupo que no se pudo leer no es un dato: el formulario sigue y decide el backend.
                if (!this._unmounted && !clientQuota.error) this.setState({clientQuota});
            });
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
    }

    // Quien abre el modal puede pasar el cupo (AgencyTeam lo trae de `agency/team`) y además está
    // el lector compartido: alcanza con que UNO diga que no hay lugar (la prop puede haber quedado
    // vieja tras un refetch). Un rechazo del backend por límite gana sobre los dos. Cupo
    // desconocido = hay lugar: decide el backend.
    canAddClientUser() {
        if (this.state.limitRejected) return false;
        if (this.props.canAddClientUser === false) return false;
        const quota = this.state.clientQuota;
        return !(quota && quota.canAddClientUser === false);
    }

    // El cupo compartido (`get-logged-user-data/`) se refresca después de un rechazo por límite; la
    // prop de `AgencyTeam` (`agency/team`) quedó como estaba al cargar la página. Por eso, si hay un
    // cupo compartido válido, gana él: la prop sola dejaba «2/5» al lado de «Alcanzaste el límite»
    // (QA-cupo-10b). En edición no se lee el cupo compartido y manda la prop.
    clientUsersLimitInfo() {
        const quota = this.state.clientQuota;
        if (quota && !quota.error && quota.max !== null) return {current: quota.current, max: quota.max};
        if (this.props.clientUsersLimitInfo) return this.props.clientUsersLimitInfo;
        return quota ? {current: quota.current, max: quota.max} : {current: null, max: null};
    }

    // Tras un rechazo por límite, un contador que todavía muestra lugar («2/5») contradice la
    // tarjeta: pasa si el refresco del cupo falló. En ese caso el contador no se muestra.
    limitUsageContradicts(limitInfo) {
        return this.state.limitRejected && limitInfo.max != null && limitInfo.current != null
            && limitInfo.current < limitInfo.max;
    }

    // El backend RECHAZÓ la invitación por límite: el email era nuevo. Queda sólo la tarjeta, sin
    // el formulario — así no hay un «Enviar» a mano con el email del cliente todavía tipeado y otro
    // tipo de usuario seleccionado (desde el equipo, eso invitaba al cliente como Admin: C-CUPO-02).
    // Reabrir el modal lo reinicia.
    showsOnlyLimitNotice() {
        return !this.props.editUser && this.state.limitRejected;
    }

    // Cupo lleno CONOCIDO con el cliente fijo (tab Portal, conexiones): el formulario sigue, con
    // el límite ENCIMA. Sumarle este cliente a alguien que ya es usuario del portal no consume
    // cupo (`GrantPortalAccess`), así que no se puede cerrar la puerta de antemano (C-CUPO-01).
    showsLimitNoticeAboveForm() {
        return !this.props.editUser && this.props.fixedClientId != null && !this.canAddClientUser();
    }

    handleSubmit(event) {
        event.preventDefault();
        const { t } = this.props;
        // Multi-cliente: un usuario del portal puede acceder a VARIOS clientes; lo único
        // inválido es no elegir ninguno.
        if (this.state.userType === 'Client' && this.state.selectedClientIds.length < 1) {
            toast.error(t('invite_user.client_requires_at_least_one'));
            return;
        }
        if (this.props.editUser) {
            this.handleEditSubmit();
        } else {
            this.handleInviteSubmit(event);
        }
    }

    handleInviteSubmit(event) {
        this.setState({saving: true});
        const data = {
            'email': event.target.email.value,
            'user_type': this.state.userType,
            'can_use_marketing_module': true,
            'can_use_manage_accounts_module': false,
            'module_marketing_can_assign_clients': this.state.moduleMarketingCanAssignClients,
            'module_manage_accounts_google_manager_id': this.state.moduleManageAccountsGoogleManagerId,
            'module_manage_accounts_can_edit_manager_adspent_limit': this.state.moduleManageAccountsCanEditManagerAdspentLimit,
            'module_manage_accounts_can_create_ad_accounts': this.state.moduleManageAccountsCanCreateAdAccounts,
            'module_manage_accounts_can_give_access_to_ad_accounts': this.state.moduleManageAccountsCanGiveAccessToAdAccounts,
            'client_ids': this.state.selectedClientIds,
            'client_features': this.state.userType === 'Client' ? this.state.clientFeatures : undefined,
            // Sólo si quien invita tocó el selector (`learningRolesToSend`); si no, la persona lo elige
            // al entrar (contrato §3).
            'learning_roles': this.learningRolesToSend(),
            'language': this.portalLanguageToSend()
        };
        const sentPortalLanguage = data.language;
        new APIClient(API_URL + 'invitation/send').call('POST', data).then((data) => {
            this.setState({saving: false});
            if (data.status === 'OK') {
                // El backend decide: si el email ya era un usuario del portal de la agencia,
                // le SUMÓ los clientes a su misma cuenta (sin invitación ni login nuevo).
                const result = data.data || {};
                if (result.attached_user_id && result.already_had_access) {
                    // Ya tenía acceso a estos clientes: no se sumó nada y no se le mandó mail.
                    // Decir "acceso agregado" acá es un éxito falso.
                    toast.info(this.props.t('invite_user.already_had_access'));
                } else {
                    toast.success(this.props.t(result.attached_user_id ? 'invite_user.attach_success' : 'invite_user.invite_sent_success'));
                }
                // El idioma que viajó: con un solo cliente, el backend lo dejó como el de su portal. Salvo
                // si el email ya era de un usuario del portal (`attached`): ahí se le sumó el cliente sin
                // invitación nueva y el idioma no se guardó (review C-portal-idioma-09).
                this.props.afterSubmit({portalLanguage: sentPortalLanguage, attached: !!result.attached_user_id})
            } else {
                // TODO lo que no sea OK es un error que el usuario tiene que ver. NO se compara
                // contra 'ERROR': el backend manda el código tipado en `status`
                // (EMAIL_IS_AGENCY_USER, CLIENT_USERS_LIMIT_REACHED, MISSING_CLIENT...), así que
                // ese chequeo dejaba el spinner volviendo al botón sin decir una palabra.
                const code = data && (data.error_code || data.status);
                if (code === 'CLIENT_USERS_LIMIT_REACHED') {
                    // No es un error a leer y cerrar: es el límite del plan, y tiene salida. Se
                    // muestra sólo la tarjeta con «Mejorar plan» y se refresca el cupo para el
                    // resto de la pantalla.
                    this.setState({limitRejected: true});
                    refetchPortalSubscriptionGating().then((clientQuota) => {
                        // El backend acaba de confirmar el LÍMITE: un cupo que diga «sin portal»
                        // (o que no se pudo leer) no puede reemplazar al que había.
                        if (!this._unmounted && clientQuota && !clientQuota.error && clientQuota.hasPortal) {
                            this.setState({clientQuota});
                        }
                    });
                    return;
                }
                toast.error(this.props.t(errorMessageKeyFor(data)));
            }
        });
    }

    // Al reescribir el email se cae el aviso: sin esto, corregir el email con el boton ya
    // apagado no vuelve a encenderlo y apretar Enviar no hace NADA — el mismo sintoma que este
    // archivo vino a arreglar.
    handleEmailChange() {
        if (this.state.emailLookup) this.setState({emailLookup: null});
    }

    emailBelongsToAgencyTeam() {
        return !this.props.editUser && !!(this.state.emailLookup && this.state.emailLookup.is_agency_user);
    }

    handleEmailBlur(event) {
        // Lookup informativo para el flujo del tab Portal: si el email ya es un usuario del
        // portal de ESTA agencia, avisamos que se le va a sumar el cliente a su cuenta (el
        // backend igual lo resuelve solo). Un email ajeno responde como inexistente.
        if (this.state.userType !== 'Client') return;
        const email = (event.target.value || '').trim();
        if (!email || !email.includes('@')) {
            this.setState({emailLookup: null});
            return;
        }
        new APIClient(API_URL + 'agency/portal-user-lookup/?email=' + encodeURIComponent(email))
            .call('GET').then((response) => {
                const info = response && response.status === 'OK' ? response.data : null;
                // `is_agency_user` viene con `exists: false` (aislamiento cross-agency: un email
                // de OTRA agencia responde igual que uno inexistente). Se guarda igual: es el
                // aviso que evita mandar una invitación que el backend va a rechazar con
                // EMAIL_IS_AGENCY_USER.
                const worthShowing = info && (info.exists || info.is_agency_user);
                this.setState({emailLookup: worthShowing ? info : null});
            });
    }

    handleEditSubmit() {
        const userId = this.props.editUser.id;
        this.setState({saving: true});

        const accessData = {
            'user_type': this.state.userType,
            'can_use_marketing_module': true,
            'can_use_manage_accounts_module': false,
            'module_marketing_can_assign_clients': this.state.moduleMarketingCanAssignClients,
        };

        const clientsData = {
            'clients': this.state.selectedClientIds.map(id => parseInt(id)),
        };

        const accessCall = new APIClient(API_URL + `user/${userId}/access`).call('POST', accessData);
        const clientsCall = new APIClient(API_URL + `user/${userId}/module/marketing`).call('POST', clientsData);

        const calls = [accessCall, clientsCall];

        if (this.state.userType === 'Client') {
            const featuresCall = new APIClient(API_URL + `user/${userId}/client-features`).call('POST', {
                client_features: this.state.clientFeatures
            });
            calls.push(featuresCall);
        }

        Promise.all(calls).then((results) => {
            this.setState({saving: false});
            // Mismo criterio que el alta: exito solo con 'OK'. Con `=== 'ERROR'` se escapaban
            // los codigos tipados, el NETWORK_ERROR y el 'UNAUTHORIZED' del login (que llega sin
            // `status`), y el modal festejaba un guardado que no ocurrio.
            const errorResult = results.find(r => !r || r.status !== 'OK');
            if (errorResult) {
                toast.error(this.props.t(errorMessageKeyFor(errorResult)));
            } else {
                toast.success(this.props.t('edit_user.saved_success'));
                this.props.afterSubmit();
            }
        });
    }

    selectUserType(type) {
        const previousType = this.state.userType;
        const switchingToOrFromClient = type === 'Client' || previousType === 'Client';
        this.setState({
            userType: type,
            selectedClientIds: switchingToOrFromClient ? [] : this.state.selectedClientIds,
            learningRoles: learningRolesForType(this.state.learningRoles, type)
        });
    }

    // «¿Qué va a hacer en la agencia?»: sólo con Aprende Dashbo prendido (lo decide quien abre el
    // modal), al INVITAR a alguien del equipo. Con cliente fijo (pestaña Portal, «Invitar al cliente a
    // conectar») el formulario es del portal y no lo muestra.
    showsLearningProfile() {
        return !!this.props.showLearningProfile && !this.props.editUser && !this.props.clientOnly
            && this.props.fixedClientId == null
            && (this.state.userType === 'Admin' || this.state.userType === 'Regular');
    }

    // El perfil viaja SÓLO si quien invita tocó el selector (decisión de fede, 04/10, review
    // C-apr-fe-equipo-02): el backend toma el perfil de la invitación como ELEGIDO, así que mandar el
    // Liderazgo preelegido sin que nadie lo mirara le sacaba al Admin invitado la pregunta del perfil.
    // Sin tocarlo, la persona elige al entrar (con Liderazgo preelegido por la página).
    learningRolesToSend() {
        if (!this.showsLearningProfile() || !this.state.learningRolesTouched) return undefined;
        if (this.state.learningRoles.length === 0) return undefined;
        return this.state.learningRoles;
    }

    // El idioma se elige al invitar al portal de UN cliente (pestaña Portal, «Invitar al cliente a
    // conectar»), donde quien abre el formulario sabe en qué idioma está ese portal y lo trae elegido. En
    // Usuarios de la agencia no se muestra: con el cliente sin elegir, un «Español» preelegido podía no
    // ser el idioma con el que el backend manda la invitación (el del portal del cliente). Tampoco si el
    // email ya es de alguien que usa el portal: a esa persona se le suma el cliente y conserva su idioma.
    showsPortalLanguage() {
        return !this.props.editUser && this.props.fixedClientId != null && this.state.userType === 'Client'
            && !(this.state.emailLookup && this.state.emailLookup.exists);
    }

    // Viaja SÓLO si quien invita lo tocó: el backend recuerda el idioma elegido como el del portal del
    // cliente. Sin tocarlo, el backend usa el del portal del cliente, que es el que se muestra elegido.
    portalLanguageToSend() {
        if (!this.showsPortalLanguage() || !this.state.portalLanguageTouched) return undefined;
        return this.state.portalLanguage;
    }

    renderPortalLanguage() {
        const { t } = this.props;
        return (
            <div className="form-row invite-portal-language">
                <label htmlFor="invite-portal-language">{t('invite_user.portal_language')}</label>
                <span className="invite-hint">{t('invite_user.portal_language_help')}</span>
                <select id="invite-portal-language" name="portalLanguage" value={this.state.portalLanguage}
                        onChange={(e) => this.setState({portalLanguage: e.target.value, portalLanguageTouched: true})}>
                    {/* Cada idioma escrito en su propia lengua, sin traducir. */}
                    {PORTAL_LANGUAGES.map((option) => (
                        <option key={option.code} value={option.code} lang={option.code}>{option.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    renderSaveButton() {
        const { t } = this.props;
        if (!this.state.saving) {
            const label = this.props.editUser ? t('edit_user.save_button') : t('invite_user.send_button');
            // El aviso ambar ya dice que el POST va a fallar: se apaga el boton y se ahorra el viaje.
            return <input type="submit" value={label} disabled={this.emailBelongsToAgencyTeam()}
                          className={this.props.editUser ? "btn -alt" : "btn -submit"}/>
        } else {
            return <Spinner color="positive small"/>;
        }
    }

    render() {
        const { t } = this.props;
        const isEditMode = !!this.props.editUser;
        if (this.showsOnlyLimitNotice()) {
            const limitInfo = this.clientUsersLimitInfo();
            return (
                <ModalBody>
                    <div className="modal-section">
                        <ClientUsersLimitNotice current={limitInfo.current} max={limitInfo.max}
                                                hideUsage={this.limitUsageContradicts(limitInfo)}/>
                    </div>
                </ModalBody>
            );
        }
        const limitInfoAbove = this.showsLimitNoticeAboveForm() ? this.clientUsersLimitInfo() : null;
        return (
            <form onSubmit={this.handleSubmit}>
                <ModalBody>
                    <div className="modal-section">
                        {limitInfoAbove && (
                            <ClientUsersLimitNotice className="invite-limit-above-form" existingUserHint
                                                    current={limitInfoAbove.current} max={limitInfoAbove.max}/>
                        )}
                        {isEditMode ? (
                            <div style={{marginBottom: '1.2em'}}>
                                <strong>{this.props.editUser.name}</strong>
                                <div style={{fontSize: '0.85em', color: '#999'}}>{this.props.editUser.email}</div>
                            </div>
                        ) : (
                            <div className="form-row">
                                <label>{t('invite_user.email_label')}</label>
                                <input name="email" type="email" required placeholder={t('invite_user.email_placeholder')}
                                       autoFocus={!!this.props.autoFocusEmail}
                                       onBlur={this.handleEmailBlur} onChange={this.handleEmailChange} />
                            </div>
                        )}

                        {!isEditMode && this.state.userType === 'Client' && this.state.emailLookup && (
                            <div className={`invite-info-callout ${this.state.emailLookup.is_agency_user ? '-warning' : ''}`}>
                                <FaIcon iconName={this.state.emailLookup.is_agency_user ? 'exclamation-triangle' : 'info-circle'}
                                        className="invite-info-callout-icon"/>
                                <span>
                                    {this.state.emailLookup.is_agency_user ? (
                                        <>
                                            {t('invite_user.email_is_agency_user_notice')}
                                            {' '}
                                            {t('invite_user.email_is_agency_user_explainer')}
                                        </>
                                    ) : (
                                        <>
                                            {t('invite_user.existing_user_notice', {
                                                name: this.state.emailLookup.name || this.state.emailLookup.client_names.join(', '),
                                                clients: (this.state.emailLookup.client_names || []).join(', '),
                                                interpolation: {escapeValue: false},
                                            })}
                                            {' '}
                                            {t('invite_user.existing_user_explainer')}
                                        </>
                                    )}
                                </span>
                            </div>
                        )}

                        <div className="invite-separator" />

                        {this.renderUserTypeCards()}
                        {this.showsLearningProfile() && (
                            <LearningProfilePicker userType={this.state.userType}
                                                   roles={this.state.learningRoles}
                                                   onChange={(learningRoles) => this.setState({learningRoles, learningRolesTouched: true})}/>
                        )}
                        {this.renderTypeOptions()}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {this.renderSaveButton()}
                </ModalFooter>
            </form>
        )
    }

    handleChange(event) {
        const target = event.target;
        const name = target.name;
        const switchesNames = ["canUseMarketingModule", "canUseManageAccountsModule", "moduleMarketingCanAssignClients",
            "moduleManageAccountsCanEditManagerAdspentLimit",
            "moduleManageAccountsCanCreateAdAccounts",
            "moduleManageAccountsCanGiveAccessToAdAccounts"]
        const value = switchesNames.includes(name) ? target.checked : target.value;

        this.setState({
            ...this.state,
            [name]: value
        });
    }

    fetchAgencyClients() {
        this.setState({loadingClients: true});
        new APIClient(API_URL + 'agency/clients/').call('GET').then((data) => {
            if (data.status === 'OK') {
                this.setState({agencyClients: data.data, loadingClients: false});
            } else {
                this.setState({loadingClients: false});
            }
        });
    }

    handleClientsChange(selectedClientIds) {
        this.setState({selectedClientIds: selectedClientIds});
    }

    handleSingleClientChange(clientId, clientName) {
        this.setState({selectedClientIds: clientId ? [clientId] : []});
    }

    renderClientCardDisabled() {
        const limitInfo = this.clientUsersLimitInfo();
        // Sólo invitando clientes (`clientOnly`) el formulario sigue en tipo Cliente, así que ahí
        // tiene sentido decir que a un usuario del portal existente se le puede sumar el cliente.
        // En edición no se mide: no es alguien chocando con el límite al invitar.
        return <ClientUsersLimitNotice current={limitInfo.current} max={limitInfo.max}
                                       existingUserHint={!!this.props.clientOnly && !this.props.editUser}
                                       hideUsage={this.limitUsageContradicts(limitInfo)}
                                       track={!this.props.editUser}/>;
    }

    renderUserTypeCards() {
        // Con cliente fijo, el tipo es siempre Cliente: no mostramos el selector de tipo.
        if (this.props.fixedClientId != null) {
            return null;
        }
        const { t } = this.props;
        const types = [];
        if (!this.props.clientOnly) {
            types.push({value: 'Admin', icon: 'user-shield', label: t('invite_user.admin_option'), desc: t('invite_user.admin_description')});
            if (this.props.moduleAccessConfigurationEnabled) {
                types.push({value: 'Regular', icon: 'user', label: t('invite_user.regular_option'), desc: t('invite_user.regular_description')});
            }
        }
        // En edición se respeta lo que pasa el modal (el cupo no se lee: el usuario editado puede
        // ya ser Cliente); en el alta, el cupo compartido.
        const clientDisabled = this.props.editUser
            ? this.props.canAddClientUser === false
            : !this.canAddClientUser();

        return (
            <div className="form-row" style={{marginBottom: '1em'}}>
                <label>{t('invite_user.user_type_label')}<LinkToArticle article={HELP_ADMIN_AND_REGULAR_USERS} /></label>
                <div className="invite-type-cards">
                    {types.map(type => (
                        <div
                            key={type.value}
                            className={`invite-type-card ${this.state.userType === type.value ? '-selected' : ''}`}
                            onClick={() => this.selectUserType(type.value)}
                        >
                            <div className="invite-type-card-icon">
                                <FaIcon iconName={type.icon}/>
                            </div>
                            <div className="invite-type-card-content">
                                <span className="invite-type-card-label">{type.label}</span>
                                <span className="invite-type-card-desc">{type.desc}</span>
                            </div>
                        </div>
                    ))}
                    {clientDisabled ? this.renderClientCardDisabled() : (
                        <div
                            className={`invite-type-card ${this.state.userType === 'Client' ? '-selected' : ''}`}
                            onClick={() => this.selectUserType('Client')}
                        >
                            <div className="invite-type-card-icon">
                                <FaIcon iconName="user-tag"/>
                            </div>
                            <div className="invite-type-card-content">
                                <span className="invite-type-card-label">{t('invite_user.client_option')}</span>
                                <span className="invite-type-card-desc">{t('invite_user.client_description_short')}</span>
                            </div>
                        </div>
                    )}
                </div>
                {this.state.userType === 'Regular' && this.props.moduleAccessConfigurationEnabled && (
                    <div className="invite-card-expand">
                        <label style={{marginBottom: 0}}>{t('invite_user.can_modify_clients')}</label>
                        <Switch onChange={this.handleChange}
                                checked={this.state.moduleMarketingCanAssignClients}
                                name="moduleMarketingCanAssignClients"
                                messageToAlertUserIfChecked={null}
                        />
                    </div>
                )}
            </div>
        );
    }

    handleFeatureToggle(featureKey, checked) {
        // Con el cliente fijo los permisos son los del portal y la lista va de sólo lectura: nada
        // puede reescribirlos. `ClientFeatureToggles` apaga `connect` solo cuando no puede
        // confirmar el módulo MCP de la agencia (también si `access/modules` falla), y ese `false`
        // viajaba en la invitación y quedaba como excepción fija del usuario: el bug de «Invitar al
        // cliente a conectar» por otra puerta (review C-rec-fe-textos-06).
        if (this.props.fixedClientId != null) return;
        this.setState({
            clientFeatures: {
                ...this.state.clientFeatures,
                [featureKey]: checked
            }
        });
    }

    renderClientFeatureToggles() {
        const { t } = this.props;
        // Desde el Portal del cliente (cliente fijo) los permisos los manda el portal:
        // se muestran read-only + nota. Desde /agency-team siguen siendo editables —
        // salvo con VARIOS clientes seleccionados: ahí los permisos son per-(usuario,
        // cliente) y los define el portal de cada uno.
        const isPortalFlow = this.props.fixedClientId != null;
        const isMultiSelection = this.state.userType === 'Client' && this.state.selectedClientIds.length > 1;
        return (
            <div className="invite-client-features-wrap">
                {/* Con el cliente fijo la lista es lo que la persona RECIBE (va de sólo lectura), así
                    que se dice qué pasa al enviar: un correo con el acceso y, desde el portal, esto. */}
                <label>{t(isPortalFlow ? 'invite_user.what_happens' : 'invite_user.client_features_label')}</label>
                <ClientFeatureToggles
                    features={this.state.clientFeatures}
                    onChange={(key, checked) => this.handleFeatureToggle(key, checked)}
                    readOnly={isPortalFlow || isMultiSelection}
                />
                {/* La nota dice DE DÓNDE salen los permisos, y eso depende de quién siembra: la
                    pestaña Portal manda los del portal tal cual; «Invitar al cliente a conectar»
                    les suma «Gestionar sus conexiones» y trae su propia nota (`featuresNote`). */}
                {isPortalFlow && (
                    <p className="field-footnote invite-portal-modules-note">
                        {this.props.featuresNote || t('invite_user.portal_modules_readonly_note')}
                    </p>
                )}
                {!isPortalFlow && isMultiSelection && (
                    <p className="field-footnote invite-portal-modules-note">
                        {t('invite_user.multi_features_note')}
                    </p>
                )}
            </div>
        );
    }

    canUserModifyClients() {
        return this.state.userType === 'Admin' ||
            (this.state.userType === 'Regular' && this.state.moduleMarketingCanAssignClients);
    }

    renderTypeOptions() {
        const { t } = this.props;
        const isClientType = this.state.userType === 'Client';
        const hasFixedClient = this.props.fixedClientId != null;

        return (
            <div key="client-options" className="invite-client-section">
                {/* Con el cliente fijo el aviso habla de «este cliente»; desde Usuarios de la
                    agencia todavía no se eligió ninguno (y pueden ser varios), así que lleva el suyo. */}
                {isClientType && (
                    <div className="invite-info-callout">
                        <FaIcon iconName="info-circle" className="invite-info-callout-icon"/>
                        <span>{t(hasFixedClient
                            ? 'invite_user.client_user_description'
                            : 'invite_user.client_user_description_team')}</span>
                    </div>
                )}
                {isClientType && this.state.selectedClientIds.length >= 1 && (
                    <a
                        className="invite-preview-link"
                        href={`/client/${this.state.selectedClientIds[0]}/preview`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FaIcon iconName="eye"/>
                        {t('preview.invite_link')}
                    </a>
                )}
                {!hasFixedClient && (
                    <div className="form-row" style={{marginBottom: 0}}>
                        <label>{isClientType ? t('invite_user.assign_clients_client_label') : t('invite_user.assign_clients_label')}</label>
                        {!isClientType && this.canUserModifyClients() && (
                            <span className="invite-hint">{t('invite_user.assign_clients_preselection_hint')}</span>
                        )}
                        {this.state.loadingClients ? (
                            <div style={{padding: '1.5em 0', textAlign: 'center'}}><Spinner color="positive small"/></div>
                        ) : this.state.agencyClients !== null ? (
                            <MultiSelect
                                showOnlyActiveCheck={true}
                                items={this.state.agencyClients}
                                selectedItemIds={this.state.selectedClientIds}
                                onChange={this.handleClientsChange}
                                itemDataMap={(item) => ({name: item.name, id: item.id.toString(), isActive: item.active})}
                            />
                        ) : null}
                    </div>
                )}
                {this.showsPortalLanguage() && this.renderPortalLanguage()}
                {isClientType && this.renderClientFeatureToggles()}
            </div>
        );
    }
}

class InviteUserModalClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            modal: false
        };

        this.toggle = this.toggle.bind(this);
        this.afterSubmit = this.afterSubmit.bind(this);
    }

    toggle() {
        this.setState({
            modal: !this.state.modal
        });
    }

    afterSubmit() {
        this.toggle();
        this.props.onUserInvited();
    }

    render() {
        const { t } = this.props;
        return (
            <div>
                <Button className="btn -alt" onClick={this.toggle}>
                    <FaIcon iconName="user-plus"/>
                    {t('invite_user.invite_button')}
                </Button>
                <Modal isOpen={this.state.modal} toggle={this.toggle} className={this.props.className}>
                    <ModalHeader toggle={this.toggle}><FaIcon iconName="user-plus"/>{t('invite_user.modal_title')}</ModalHeader>
                    <InviteUserForm closeModal={this.toggle} afterSubmit={this.afterSubmit}
                                    moduleAccessConfigurationEnabled={this.props.moduleAccessConfigurationEnabled}
                                    canAddClientUser={this.props.canAddClientUser}
                                    clientUsersLimitInfo={this.props.clientUsersLimitInfo}
                                    clientOnly={this.props.clientOnly}
                                    isSuperAdmin={this.props.isSuperAdmin}
                                    showLearningProfile={this.props.showLearningProfile}/>
                </Modal>
            </div>
        );
    }
}

class EditUserModalClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            modal: false
        };
        this.toggle = this.toggle.bind(this);
        this.afterSubmit = this.afterSubmit.bind(this);
    }

    toggle() {
        this.setState({modal: !this.state.modal});
    }

    afterSubmit() {
        this.toggle();
        if (this.props.onSaved) this.props.onSaved();
    }

    render() {
        const { t, user } = this.props;
        return (
            <>
                <a onClick={this.toggle} style={{cursor: 'pointer'}} title={t('edit_user.edit_button_title')}>
                    <FaIcon iconName="user-edit"/>
                </a>
                <Modal isOpen={this.state.modal} toggle={this.toggle}>
                    <ModalHeader toggle={this.toggle}>
                        <FaIcon iconName="user-edit"/>
                        {t('edit_user.modal_title')}
                    </ModalHeader>
                    <InviteUserForm
                        closeModal={this.toggle}
                        afterSubmit={this.afterSubmit}
                        editUser={user}
                        clientUsersLimitInfo={this.props.clientUsersLimitInfo}
                        moduleAccessConfigurationEnabled={this.props.moduleAccessConfigurationEnabled}
                        canAddClientUser={this.props.canAddClientUser}
                        isSuperAdmin={this.props.isSuperAdmin}
                    />
                </Modal>
            </>
        );
    }
}

export const InviteUserForm = withTranslation()(InviteUserFormClass);
export const InviteUserModal = withTranslation()(InviteUserModalClass);
export const EditUserModalInline = withTranslation()(EditUserModalClass);
